import { useMemo } from "react";

import { isDevelopment } from "../../utils/devWarning";
import { useDevWarnOnce } from "../../utils/useDevWarnOnce";
import { isMediaTimelineSegmentTone, validateMediaTimelineSegments } from "./mediaTimelineSegments";
import { isSeriesDomain, validateMediaTimelineSeries } from "../TimeSeriesChart/seriesData";
import type {
  MediaTimelineSeries,
  MediaTimelineTrack,
  MediaTimelineSegment,
} from "./timelineTypes";

type SegmentDiagnostics = { outOfBounds: boolean; invalid: boolean; reversed: boolean };
type SeriesDiagnostics = { invalid: boolean; unsorted: boolean };
const EMPTY_SEGMENTS: readonly MediaTimelineSegment[] = [];

/** Bounded development diagnostics for a bar's tracks (bands, series, lanes, axes); never a per-item warning/history cache. */
export function useMediaTimelineTrackWarnings(
  tracks: readonly MediaTimelineTrack[],
  durationSeconds: number,
): void {
  const cache = useMemo(
    () => ({
      durationSeconds,
      values: new WeakMap<readonly MediaTimelineSegment[], SegmentDiagnostics>(),
      series: new WeakMap<readonly MediaTimelineSeries[], SeriesDiagnostics>(),
    }),
    [durationSeconds],
  );
  const summary = useMemo(() => {
    const result = {
      outOfBounds: false,
      invalid: false,
      reversed: false,
      rangeTone: false,
      invalidSeries: false,
      unsortedSeries: false,
      invalidDomain: false,
      lanesWithSeries: false,
      duplicateLaneSeries: false,
    };
    if (!isDevelopment()) return result;
    const options =
      Number.isFinite(cache.durationSeconds) && cache.durationSeconds > 0
        ? { durationSeconds: cache.durationSeconds }
        : {};
    for (const track of tracks) {
      if (track.type !== "track") continue;
      result.rangeTone ||=
        track.selection?.tone !== undefined && !isMediaTimelineSegmentTone(track.selection.tone);
      result.lanesWithSeries ||= track.lanes !== undefined && track.series !== undefined;
      const sources = track.lanes
        ? track.lanes.map((lane) => lane.series)
        : track.series
          ? [track.series]
          : [];
      if (track.lanes && track.lanes.length > 1) {
        const ids = new Set<string>();
        for (const lane of track.lanes) {
          for (const entry of lane.series) {
            if (typeof entry?.id !== "string") continue;
            if (ids.has(entry.id)) result.duplicateLaneSeries = true;
            ids.add(entry.id);
          }
        }
      }
      for (const source of sources) {
        let diagnostics = cache.series.get(source);
        if (!diagnostics) {
          diagnostics = { invalid: false, unsorted: false };
          for (const issue of validateMediaTimelineSeries(source)) {
            if (issue.code === "unsorted-time") diagnostics.unsorted = true;
            else diagnostics.invalid = true;
          }
          cache.series.set(source, diagnostics);
        }
        result.invalidSeries ||= diagnostics.invalid;
        result.unsortedSeries ||= diagnostics.unsorted;
      }
      if (sources.length > 0) {
        result.invalidDomain ||= track.domain !== undefined && !isSeriesDomain(track.domain);
      }
      for (const lane of track.lanes ?? []) {
        result.invalidDomain ||= lane.domain !== undefined && !isSeriesDomain(lane.domain);
      }
      const source = track.segments ?? EMPTY_SEGMENTS;
      let diagnostics = cache.values.get(source);
      if (!diagnostics) {
        diagnostics = { outOfBounds: false, invalid: false, reversed: false };
        for (const issue of validateMediaTimelineSegments(source, options)) {
          if (issue.code === "out-of-bounds") diagnostics.outOfBounds = true;
          else if (issue.code === "reversed-range") diagnostics.reversed = true;
          else diagnostics.invalid = true;
        }
        cache.values.set(source, diagnostics);
      }
      result.outOfBounds ||= diagnostics.outOfBounds;
      result.invalid ||= diagnostics.invalid;
      result.reversed ||= diagnostics.reversed;
    }
    return result;
  }, [tracks, cache]);
  useDevWarnOnce(
    summary.outOfBounds,
    "[MediaTimelineBar] Segment edges exceed media bounds. Display spans are clamped; fully outside spans are omitted. Source data is unchanged. Use validateMediaTimelineSegments to locate inputs.",
  );
  useDevWarnOnce(
    summary.invalid,
    "[MediaTimelineBar] Invalid segments (id, tone, finite times or empty span) are omitted. Use validateMediaTimelineSegments to locate inputs; correct source data in the consumer.",
  );
  useDevWarnOnce(
    summary.rangeTone,
    '[MediaTimelineBar] A range tone must be "dataViz.<hue>"; status tones such as "success" and "accent" are not accepted (a trim keeps its green with "dataViz.green"). Unsupported tones paint as "dataViz.blue".',
  );
  useDevWarnOnce(
    summary.reversed,
    "[MediaTimelineBar] Reversed segment edges are ordered for display. Source data is unchanged. Use validateMediaTimelineSegments to locate inputs.",
  );
  useDevWarnOnce(
    summary.invalidSeries,
    "[MediaTimelineBar] Invalid series (id, tone, points array, interpolation) or samples without a finite time are omitted. Use validateMediaTimelineSeries to locate inputs; correct source data in the consumer.",
  );
  useDevWarnOnce(
    summary.unsortedSeries,
    "[MediaTimelineBar] Series samples are not in ascending time and were ordered for display. Source data is unchanged. Use validateMediaTimelineSeries to locate inputs.",
  );
  useDevWarnOnce(
    summary.invalidDomain,
    "[MediaTimelineBar] A series row or lane domain must be finite with min < max; the data extent (or the row axis, for a lane) is used instead.",
  );
  useDevWarnOnce(
    summary.lanesWithSeries,
    "[MediaTimelineBar] A row declares both `lanes` and `series`; `lanes` is drawn and `series` is ignored. Put the plain series in a single lane or drop one of them.",
  );
  useDevWarnOnce(
    summary.duplicateLaneSeries,
    "[MediaTimelineBar] Series ids repeat across a row's lanes. Ids stay unique per row so a values popup row and a legend entry name one line.",
  );
}
